/**
 * TypeScript models for Observer Configuration
 * Mirrors the C# domain model structure
 */

export type ObserverDetailLevel = 'compact' | 'detailed' | 'verbose';

export interface IEntityTypeFilter {
  includeProcess: boolean;
  includeThread: boolean;
  includeNode: boolean;
  includeConfigResolution: boolean;
  includeExpressions: boolean;
  includePolicies: boolean;
  includeGuardRails: boolean;
  includeRouting: boolean;
}

export interface IEventTypeFilter {
  includeStarting: boolean;
  includeExecuting: boolean;
  includeCompleted: boolean;
  includePaused: boolean;
  includeErrored: boolean;
  errorsOnly: boolean;
}

export interface IEntityIdFilter {
  processIds?: number[];
  executionId?: string;
  threadId?: string;
  nodeKey?: string;
  nodeKeys?: string[];
}

export interface IPerformanceFilter {
  minDurationMs?: number;
  maxDurationMs?: number;
  slowThresholdMs?: number;
}

export interface IExecutionEventFilter {
  entityTypes?: IEntityTypeFilter;
  eventTypes?: IEventTypeFilter;
  entityIds?: IEntityIdFilter;
  performance?: IPerformanceFilter;
}

export interface IExecutionObserverConfig {
  enabled: boolean;
  detailLevel: ObserverDetailLevel;
  filter?: IExecutionEventFilter;
}

/**
 * Concrete implementation of observer configuration
 */
export interface ExecutionObserverConfig extends IExecutionObserverConfig {
  enabled: boolean;
  detailLevel: ObserverDetailLevel;
  filter?: ExecutionEventFilter;
}

export interface ExecutionEventFilter extends IExecutionEventFilter {
  entityTypes?: EntityTypeFilter;
  eventTypes?: EventTypeFilter;
  entityIds?: EntityIdFilter;
  performance?: PerformanceFilter;
}

export interface EntityTypeFilter extends IEntityTypeFilter {
  includeProcess: boolean;
  includeThread: boolean;
  includeNode: boolean;
  includeConfigResolution: boolean;
  includeExpressions: boolean;
  includePolicies: boolean;
  includeGuardRails: boolean;
  includeRouting: boolean;
}

export interface EventTypeFilter extends IEventTypeFilter {
  includeStarting: boolean;
  includeExecuting: boolean;
  includeCompleted: boolean;
  includePaused: boolean;
  includeErrored: boolean;
  errorsOnly: boolean;
}

export interface EntityIdFilter extends IEntityIdFilter {
  processIds?: number[];
  executionId?: string;
  threadId?: string;
  nodeKey?: string;
  nodeKeys?: string[];
}

export interface PerformanceFilter extends IPerformanceFilter {
  minDurationMs?: number;
  maxDurationMs?: number;
  slowThresholdMs?: number;
}

/**
 * Fully-populated defaults, used wherever a partial/missing filter needs to be
 * merged into a complete object (all EntityTypeFilter/EventTypeFilter fields
 * are required booleans, so a bare `{}` fallback does not satisfy the type).
 */
export const DEFAULT_ENTITY_TYPE_FILTER: EntityTypeFilter = {
  includeProcess: true,
  includeThread: true,
  includeNode: true,
  includeConfigResolution: false,
  includeExpressions: false,
  includePolicies: false,
  includeGuardRails: false,
  includeRouting: false,
};

export const DEFAULT_EVENT_TYPE_FILTER: EventTypeFilter = {
  includeStarting: true,
  includeExecuting: true,
  includeCompleted: true,
  includePaused: true,
  includeErrored: true,
  errorsOnly: false,
};

/**
 * Saved configuration metadata
 */
export interface SavedObserverConfig {
  id: string;
  name: string;
  description: string;
  config: ExecutionObserverConfig;
  createdAt: Date;
  lastUsedAt?: Date;
  useCount: number;
}

/**
 * Factory for creating preset configurations
 */
export class ExecutionObserverConfigFactory {
  /**
   * Create default configuration (all enabled)
   */
  static createDefault(): ExecutionObserverConfig {
    return {
      enabled: true,
      detailLevel: 'detailed',
      filter: {
        entityTypes: {
          includeProcess: true,
          includeThread: true,
          includeNode: true,
          includeConfigResolution: true,
          includeExpressions: true,
          includePolicies: true,
          includeGuardRails: true,
          includeRouting: true,
        },
        eventTypes: {
          includeStarting: true,
          includeExecuting: true,
          includeCompleted: true,
          includePaused: true,
          includeErrored: true,
          errorsOnly: false,
        },
      },
    };
  }

  /**
   * Create basic configuration (only main entity/event types)
   */
  static createBasic(): ExecutionObserverConfig {
    return {
      enabled: true,
      detailLevel: 'detailed',
      filter: {
        entityTypes: {
          includeProcess: true,
          includeThread: true,
          includeNode: true,
          includeConfigResolution: false,
          includeExpressions: false,
          includePolicies: false,
          includeGuardRails: false,
          includeRouting: false,
        },
        eventTypes: {
          includeStarting: true,
          includeExecuting: true,
          includeCompleted: true,
          includePaused: true,
          includeErrored: true,
          errorsOnly: false,
        },
      },
    };
  }

  /**
   * Create debug configuration (all details, verbose)
   */
  static createDebug(): ExecutionObserverConfig {
    return {
      enabled: true,
      detailLevel: 'verbose',
      filter: {
        entityTypes: {
          includeProcess: true,
          includeThread: true,
          includeNode: true,
          includeConfigResolution: true,
          includeExpressions: true,
          includePolicies: true,
          includeGuardRails: true,
          includeRouting: true,
        },
        eventTypes: {
          includeStarting: true,
          includeExecuting: true,
          includeCompleted: true,
          includePaused: true,
          includeErrored: true,
          errorsOnly: false,
        },
      },
    };
  }

  /**
   * Create comprehensive configuration (everything enabled)
   */
  static createComprehensive(): ExecutionObserverConfig {
    return this.createDebug();
  }

  /**
   * Create configuration for monitoring a specific node
   */
  static createForNode(nodeKey: string): ExecutionObserverConfig {
    return {
      enabled: true,
      detailLevel: 'detailed',
      filter: {
        entityTypes: {
          includeProcess: true,
          includeThread: true,
          includeNode: true,
          includeConfigResolution: true,
          includeExpressions: true,
          includePolicies: true,
          includeGuardRails: true,
          includeRouting: true,
        },
        eventTypes: {
          includeStarting: true,
          includeExecuting: true,
          includeCompleted: true,
          includePaused: true,
          includeErrored: true,
          errorsOnly: false,
        },
        entityIds: {
          nodeKey,
        },
      },
    };
  }

  /**
   * Create configuration for monitoring a specific process
   */
  static createForProcess(processId: number): ExecutionObserverConfig {
    return {
      enabled: true,
      detailLevel: 'detailed',
      filter: {
        entityTypes: {
          includeProcess: true,
          includeThread: true,
          includeNode: true,
          includeConfigResolution: true,
          includeExpressions: true,
          includePolicies: true,
          includeGuardRails: true,
          includeRouting: true,
        },
        eventTypes: {
          includeStarting: true,
          includeExecuting: true,
          includeCompleted: true,
          includePaused: true,
          includeErrored: true,
          errorsOnly: false,
        },
        entityIds: {
          processIds: [processId],
        },
      },
    };
  }
}

export default ExecutionObserverConfig;
